import AppInsightKey from 'features/app/config/appInsightsKeys.json';
import configManager from 'features/app/config/configManager';
import { AppEnvironment, DefaultFlightMode } from 'features/app/config/configType';
import { FlightsResponseV2 } from 'services/apollo/types/generated';
import { Flight } from 'services/flights/flightList';
import * as LocalStorage from 'services/local-storage-service';

/**
 * LocalStorage key used by `setControlTowerFlights` to persist the last
 * successful `getFlightsV2` response. We re-read it synchronously here so the
 * kill-switch can be evaluated before the saga finishes.
 *
 * NOTE: Keep this in sync with `appConfigLocalStorageKey` in
 * `services/flights/utils.ts`.
 */
const FLIGHTS_LOCAL_STORAGE_KEY = 'localflights';

/**
 * SessionStorage key used to cache the resolved flights map for a short window
 * (see `SERVER_FLIGHTS_SESSION_CACHE_TTL_MS`). Scoped to the tab.
 */
export const SERVER_FLIGHTS_SESSION_CACHE_KEY = 'pmw.serverFlightsCache';

/**
 * TTL for the sessionStorage cache used when UI-driven flights are deprecated.
 * Short enough that a Control Tower change is visible within minutes, long
 * enough to dampen reload storms.
 */
export const SERVER_FLIGHTS_SESSION_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Interval used by the periodic refresh when UI-driven flights are deprecated.
 */
export const SERVER_FLIGHTS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Returns true when the current build is running against Production app
 * insights. Kept exported so call sites that legitimately need to know about
 * the environment (telemetry, logging) can use a single helper; the
 * kill-switch itself no longer depends on this.
 */
export function isProdEnvironment(): boolean {
	return configManager.getConfig().appInsightsKey === AppInsightKey.Prod;
}

export function areQuoteV2OverridesAllowed(flights: Partial<Record<Flight, boolean>>): boolean {
	const config = configManager.getConfig();
	return (
		config.appEnvironment !== AppEnvironment.Prod &&
		config.services?.flights?.defaultFlightMode !== DefaultFlightMode.Insider &&
		!areUIDrivenFlightsDeprecatedFromState(flights)
	);
}

/**
 * Reads the kill-switch flight value synchronously from the most recent
 * successful `getFlightsV2` response cached in LocalStorage. Returns `false`
 * when no cache exists yet, when the cache is malformed, or when the flag is
 * not present in the response — making both the rollout and rollback safe.
 */
function readKillSwitchFromCache(): boolean {
	try {
		const cached = LocalStorage.get<FlightsResponseV2>(FLIGHTS_LOCAL_STORAGE_KEY);
		const entry = cached?.flights?.find(f => f.name === Flight.deprecateUIDrivenFlights);
		return !!entry?.enabled;
	} catch {
		return false;
	}
}

/**
 * Synchronous, dependency-free boot evaluation of the kill-switch.
 *
 * Used by the very-first-render code paths (reducer initial state,
 * `pageLoadFlights` snapshot) that run **before** Redux exists. It only
 * inspects the last `getFlightsV2` response that was persisted to
 * LocalStorage by `setControlTowerFlights`.
 *
 * Returns true whenever the most recent server response had
 * `deprecateUIDrivenFlights` enabled, regardless of the running environment.
 * Turn the flag ON in Control Tower for any environment (Latest/Stable/Prod)
 * and the new behavior takes over there; turn it OFF and the legacy
 * behavior is preserved byte-for-byte.
 *
 * NOTE: For *behavioral* gates that run after Redux is available — the
 * Apollo request link, the LocalStorage middleware, the refresh-loop
 * trigger — prefer {@link areUIDrivenFlightsDeprecatedFromState}. That
 * reads the live store and therefore activates the new mode within the
 * **same session** as soon as the saga writes a fresh response, with no
 * page-reload lag.
 */
export function areUIDrivenFlightsDeprecated(): boolean {
	return readKillSwitchFromCache();
}

/**
 * Live, reactive evaluation of the kill-switch using the current flights
 * slice from the Redux store. This is what behavioral gates should call so
 * the mode switches over within the current session the moment the saga's
 * `getFlightsV2` response is applied — no reload required.
 *
 * Pure function: safe to call from middleware, Apollo links, and selectors.
 */
export function areUIDrivenFlightsDeprecatedFromState(
	flights: Partial<Record<Flight, boolean>> | undefined
): boolean {
	return !!flights?.[Flight.deprecateUIDrivenFlights];
}
